import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Typography, Grid } from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import accessTokenAtom from 'data/atoms/accessTokenAtom';
import {
  ICompanyViewModel,
  IDepartmentViewModel,
} from 'Interfaces/ICompaniesConfig';

import {
  CloudFactoryCompareProduct,
  CloudFactoryProductViewModel,
  removeLastColonPart,
} from '../Converts/ICloudFactoryInterfaces';
import { ImportProductList } from '../ImportProductList';
import { EnumBindingPeriod } from 'Interfaces/IGlobalTypeConfig';
import { useSnackbar } from 'notistack';
import { CloudFactoryService } from '../APi/CloudFactoryAPI';
import { toProductViewModel } from '../Converts/ToViewModel';

// 🔹 Ny: bruk felles axios-klient mot Bluu-admin API
import { api } from 'http/apiClient';

interface Props {
  open: boolean;
  company: ICompanyViewModel | null;
  onClose: () => void;
}

export const CloudFactoryProductDrawer: React.FC<Props> = ({
  open,
  company,
  onClose,
}) => {
  const { height } = useWindowsDimension();
  const [accessToken] = useAtom(accessTokenAtom);
  const [products, setProducts] = useState<
    CloudFactoryProductViewModel[] | null
  >(null);
  const [departments] = useState<IDepartmentViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Effekt for å hente og sammenligne produkter
  useEffect(() => {
    const load = async () => {
      setProducts(null);

      if (!open || !company?.thirdPartyId || !accessToken) return;

      setLoading(true);
      setError(null);

      try {
        // 1) Hent abonnementer fra CloudFactory
        const subs = await CloudFactoryService.getSubscriptions(
          company.thirdPartyId,
          accessToken
        );

        console.log('Subscriptions: subs', subs.data);

        enqueueSnackbar(
          `Subscriptions ${company.name}. funnet: ${subs.data.length}`,
          { variant: 'info' }
        );

        // 2) Hent pris/produkt for hver subscription og bygg compare-liste
        const list: CloudFactoryCompareProduct[] = [];

        for (const sub of subs.data) {
          try {
            const cat = await CloudFactoryService.getCatalogueProducts(
              company.thirdPartyId,
              removeLastColonPart(sub.sku),
              sub.billingCycle,
              sub.termDuration,
              1,
              1,
              accessToken
            );

            const item = cat.data.results[0];
            const price = item.price;
            const prod = item.product;

            console.log('Subscriptions: results', cat.data.results);
            console.log('Subscriptions: cat', cat);
            console.log('Subscriptions: sub', sub);
            console.log('Subscriptions: item', item);
            console.log('Subscriptions: prod', prod);

            const salePerMonth =
              price.sale /
              (sub.billingCycle === 'Monthly' && sub.termDuration === 'P1M'
                ? 1
                : 12);

            const unitPrice =
              price.cost /
              (sub.billingCycle === 'Monthly'
                ? 1
                : sub.termDuration === 'P1M'
                ? 1
                : 12);

            console.log(
              'Subscriptions: price',
              `${sub.billingCycle} : ${salePerMonth} PRICE : ${price.sale}`
            );

            const compareItem: CloudFactoryCompareProduct = {
              companyId: company.id,
              name: prod.name,
              description: prod.description,
              sku: removeLastColonPart(sub.sku),
              bindingPeriod:
                sub.termDuration === 'P1M'
                  ? EnumBindingPeriod.Month
                  : EnumBindingPeriod.Year,
              paymentCycle:
                sub.billingCycle === 'Monthly'
                  ? EnumBindingPeriod.Month
                  : EnumBindingPeriod.Year,
              costPrice: unitPrice,
              unitPrice: salePerMonth,
              quantity: sub.quantity,
              companyProductQty: 0,
              commitmentEndDate: sub.commitmentEndDate,
            };

            list.push(compareItem);

            enqueueSnackbar(`Pris hentet for ${sub.name}`, {
              variant: 'info',
            });
          } catch (inner) {
            console.error('CloudFactory katalog-feil:', inner);
            enqueueSnackbar(`Kunne ikke hente katalog for ${sub.name}`, {
              variant: 'error',
            });
          }
        }

        // 3) Slå sammen produkter med samme name/sku/binding/payment
        const mergedMap: Record<string, CloudFactoryCompareProduct> = {};

        list.forEach((item) => {
          const key = `${item.name}|${item.sku}|${item.bindingPeriod}|${item.paymentCycle}`;
          if (!mergedMap[key]) {
            mergedMap[key] = { ...item };
          } else {
            mergedMap[key].quantity += item.quantity;
          }
        });

        const mergedList = Object.values(mergedMap);
        console.log('Merged products:', mergedList);

        // 4) Sammenlign mot backend (Bluu-admin API)
        //    Bruker api-klienten -> Authorization + X-Tenant-Id kommer via interceptor
        const response = await api.post<{
          cloudFactoryProducts: CloudFactoryCompareProduct[];
        }>('/Module/CloudFactory/ComparedProducts', {
          cloudFactoryProducts: mergedList,
        });

        const existing = response.data.cloudFactoryProducts;

        enqueueSnackbar(
          `Sjekket ${existing.length} produkter mot databasen`,
          { variant: 'info' }
        );

        console.log('Existing products (raw):', existing);

        // 5) Konverter til CloudFactoryProductViewModel
        const filtered: CloudFactoryProductViewModel[] = existing.map(
          (p: any) => ({
            companyId: p.companyId,
            product: toProductViewModel(p),
            quantity: p.quantity,
            companyProductQuantity: p.companyProductQuantity || 0,
          })
        );

        console.log('Existing products (view models):', filtered);

        setProducts(filtered);
      } catch (err: any) {
        console.error('Feil i CloudFactoryProductDrawer.load:', err);
        setError(err.message || 'Feil under lasting');
        enqueueSnackbar('Feil under lasting av CloudFactory-produkter', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, company, accessToken, enqueueSnackbar]);

  const handleRemoveProducts = (imported: CloudFactoryProductViewModel[]) => {
    setProducts((prev) =>
      prev
        ? prev.filter(
            (p) =>
              !imported.some(
                (i) => i.product.thirdPartyId === p.product.thirdPartyId
              )
          )
        : []
    );

    enqueueSnackbar(`Fjernet ${imported.length} produkter fra importlisten`, {
      variant: 'info',
    });

    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      variant="persistent"
      PaperProps={{ sx: { width: '100%' } }}
      onClose={onClose}
    >
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            flexGrow: 1,
            fontWeight: 'bold',
            fontSize: 20,
            color: 'primary.contrastText',
          }}
        >
          IMPORT PRODUCTS
        </Typography>
        <IconButton onClick={onClose}>
          <CancelRoundedIcon />
        </IconButton>
      </Box>

      <Grid container sx={{ backgroundColor: 'primary.main' }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <ImportProductList
            expanded
            products={products || []}
            departments={departments}
            company={company}
            handleRemoveProducts={handleRemoveProducts}
          />
        </Grid>
      </Grid>
    </Drawer>
  );
};
